import { z } from 'zod';
import {
  ADDRESS_MESSAGES,
  DEFAULT_COUNTRY,
  DEFAULT_CURRENCY,
  DEFAULT_LOCALE,
  DEFAULT_TIMEZONE,
  normalizeRegion,
  SUPPORTED_COUNTRY_CODES,
  type SupportedCountry,
} from './address';
import {
  baseRow,
  email,
  hexColor,
  id,
  isoDate,
  listInput,
  nonEmpty,
  optionalText,
  partialOf,
  slug,
  updateOf,
} from './common';

// ---------------------------------------------------------------------------
// Enumerations shared by DB, API, web and mobile
// ---------------------------------------------------------------------------

export const ROLES = ['owner', 'admin', 'sales', 'pm', 'field', 'sub', 'client'] as const;
export const role = z.enum(ROLES);
export type Role = z.infer<typeof role>;

/** Roles that may be held as a workspace membership (client is portal-only via portal_grant). */
export const MEMBER_ROLES = ['owner', 'admin', 'sales', 'pm', 'field', 'sub'] as const;
export const memberRole = z.enum(MEMBER_ROLES);
export type MemberRole = z.infer<typeof memberRole>;

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  admin: 'Admin',
  sales: 'Sales / Estimator',
  pm: 'Project manager',
  field: 'Field',
  sub: 'Subcontractor (guest)',
  client: 'Client (portal)',
};

export const BUSINESS_TYPES = ['residential', 'remodeler', 'commercial', 'specialty_sub'] as const;
export const businessType = z.enum(BUSINESS_TYPES);
export type BusinessType = z.infer<typeof businessType>;

export const BUSINESS_TYPE_LABELS: Record<BusinessType, string> = {
  residential: 'Residential builder',
  remodeler: 'Remodeler',
  commercial: 'Commercial contractor / GC',
  specialty_sub: 'Specialty subcontractor',
};

/**
 * Optional trade template layered on a business type (ported from NorthOps' vertical packs).
 * It adds trade-specific pipelines, project templates and follow-up playbooks at setup.
 */
export const TRADE_TEMPLATES = ['hvac_plumbing', 'remodeling_roofing'] as const;
export const tradeTemplate = z.enum(TRADE_TEMPLATES);
export type TradeTemplate = z.infer<typeof tradeTemplate>;

export const TRADE_TEMPLATE_LABELS: Record<TradeTemplate, string> = {
  hvac_plumbing: 'HVAC / Plumbing',
  remodeling_roofing: 'Home remodeling / Roofing',
};

/** NorthOps / Hive vertical ids that map onto a BuildBee trade template. */
export const TRADE_TEMPLATE_BY_VERTICAL: Record<string, TradeTemplate> = {
  hvac: 'hvac_plumbing',
  'home-remodeling-roofing': 'remodeling_roofing',
};

/**
 * Automations (Settings > Automations): reminders for the team and follow-ups for clients, ported
 * from NorthOps' follow-up playbooks. Client-facing ones send email, so they start switched off.
 */
export const AUTOMATION_KEYS = [
  'task_reminder',
  'stale_lead_reminder',
  'proposal_follow_up',
  'proposal_expiring',
  'project_status_update',
  'maintenance_check_in',
] as const;
export const automationKey = z.enum(AUTOMATION_KEYS);
export type AutomationKey = z.infer<typeof automationKey>;

const automationMessage = z.string().trim().max(2000).nullable().optional();
const offsetDays = z.number().int().min(1).max(365);

const automationShapes = {
  /** In-app reminder to the assignee when a task's "remind me" time arrives. */
  task_reminder: z.object({ enabled: z.boolean() }),
  /** In-app nudge to the deal owner when an open lead sits idle past its stage's rotting days. */
  stale_lead_reminder: z.object({ enabled: z.boolean(), repeatDays: offsetDays }),
  /** Email the client about a sent proposal they have not signed, N days after it was sent. */
  proposal_follow_up: z.object({ enabled: z.boolean(), days: z.array(offsetDays).min(1).max(5), message: automationMessage }),
  /** Email the client a few days before a sent proposal expires. */
  proposal_expiring: z.object({ enabled: z.boolean(), daysBefore: offsetDays, message: automationMessage }),
  /** Email the client when their project starts, reaches punch, and is completed. */
  project_status_update: z.object({ enabled: z.boolean() }),
  /** Email past clients a check-in (maintenance, warranty walk-through) after a project is completed. */
  maintenance_check_in: z.object({ enabled: z.boolean(), afterDays: offsetDays, message: automationMessage }),
};

export const automationSettingsSchema = z.object({
  task_reminder: automationShapes.task_reminder.default({ enabled: true }),
  stale_lead_reminder: automationShapes.stale_lead_reminder.default({ enabled: false, repeatDays: 7 }),
  proposal_follow_up: automationShapes.proposal_follow_up.default({ enabled: false, days: [3, 7, 14] }),
  proposal_expiring: automationShapes.proposal_expiring.default({ enabled: false, daysBefore: 3 }),
  project_status_update: automationShapes.project_status_update.default({ enabled: false }),
  maintenance_check_in: automationShapes.maintenance_check_in.default({ enabled: false, afterDays: 365 }),
});
export type AutomationSettings = z.infer<typeof automationSettingsSchema>;
export const DEFAULT_AUTOMATION_SETTINGS: AutomationSettings = automationSettingsSchema.parse({});

/** Partial update: only the automations sent are replaced. */
export const updateAutomationsInput = z.object({
  task_reminder: automationShapes.task_reminder.optional(),
  stale_lead_reminder: automationShapes.stale_lead_reminder.optional(),
  proposal_follow_up: automationShapes.proposal_follow_up.optional(),
  proposal_expiring: automationShapes.proposal_expiring.optional(),
  project_status_update: automationShapes.project_status_update.optional(),
  maintenance_check_in: automationShapes.maintenance_check_in.optional(),
});
export type UpdateAutomationsInput = z.infer<typeof updateAutomationsInput>;

export const MEMBERSHIP_STATUSES = ['active', 'suspended'] as const;
export const membershipStatus = z.enum(MEMBERSHIP_STATUSES);

export const INVITATION_STATUSES = ['pending', 'accepted', 'revoked', 'expired'] as const;
export const invitationStatus = z.enum(INVITATION_STATUSES);

/** Resources the permission engine knows about. */
export const RESOURCES = [
  'workspace',
  'settings',
  'billing',
  'member',
  'permission_template',
  'custom_field',
  'audit',
  'export',
  'company',
  'contact',
  'pipeline',
  'deal',
  'activity',
  'task',
  'estimate',
  'proposal',
  'template',
  'report',
  'project',
  'project_member',
  'schedule',
  'document',
  'drawing',
  'markup',
  'rfi',
  'submittal',
  'daily_log',
  'photo',
  'punch_item',
  'selection',
  'message',
  'bid_package',
  'bid',
  'budget',
  'change_order',
  'invoice',
  'payment',
  'lien_waiver',
  'portal',
  'integration',
  'email',
  'webhook',
  'api_key',
] as const;
export const resource = z.enum(RESOURCES);
export type Resource = z.infer<typeof resource>;

export const ACTIONS = ['view', 'create', 'update', 'delete', 'approve', 'export', 'manage'] as const;
export const action = z.enum(ACTIONS);
export type Action = z.infer<typeof action>;

/** A permission string, e.g. `deal:update`, `invoice:*`, `*:view`, `*`. */
export const permission = z
  .string()
  .regex(/^(\*|[a-z_]+:(\*|[a-z_]+)|\*:[a-z_]+)$/, 'Expected resource:action');
export type Permission = `${Resource | '*'}:${Action | '*'}` | '*';

/** Permission set stored on permission templates and overrides. Deny wins over allow. */
export const permissionSet = z.object({
  allow: z.array(permission).default([]),
  deny: z.array(permission).default([]),
  /** When true, sub/field users only see records they own or are assigned to. */
  ownRecordsOnly: z.boolean().optional(),
});
export type PermissionSet = z.infer<typeof permissionSet>;

// ---------------------------------------------------------------------------
// Workspace
// ---------------------------------------------------------------------------

export const brandingSchema = z.object({
  logoKey: z.string().max(1024).nullable().optional(),
  primaryColor: hexColor.optional(),
  accentColor: hexColor.optional(),
  portalWelcome: z.string().max(2000).optional(),
  emailFooter: z.string().max(2000).optional(),
});
export type Branding = z.infer<typeof brandingSchema>;

/**
 * Workspace settings, stored sparse as JSON and read through this schema, so settings saved
 * before a field existed get its default. Defaults are Canadian (CAD, en-CA, Toronto time).
 */
export const workspaceSettingsSchema = z.object({
  timezone: z.string().max(64).default(DEFAULT_TIMEZONE),
  /** ISO 4217 code (CAD, USD); Stripe gets it lowercased. */
  currency: z.string().trim().toUpperCase().length(3).default(DEFAULT_CURRENCY),
  locale: z.string().max(16).default(DEFAULT_LOCALE),
  /** Where the business is based (two-letter code); drives address defaults. */
  country: z.string().trim().toUpperCase().length(2).default(DEFAULT_COUNTRY),
  /** Province/state whose sales tax applies (two-letter code), e.g. ON. Null/absent: not set. */
  taxRegion: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, 'Use a two-letter province or state code, e.g. ON')
    .nullable()
    .optional(),
  weekStartsOn: z.union([z.literal(0), z.literal(1)]).default(1),
  defaultTaxRateBps: z.number().int().min(0).max(10_000).default(0),
  defaultMarkupBps: z.number().int().min(0).max(100_000).default(1500),
  defaultRetainageBps: z.number().int().min(0).max(10_000).default(0),
  invoicePrefix: z.string().max(12).default('INV-'),
  estimatePrefix: z.string().max(12).default('EST-'),
  projectPrefix: z.string().max(12).default('P-'),
  changeOrderPrefix: z.string().max(12).default('CO-'),
  paymentTermsDays: z.number().int().min(0).max(365).default(30),
  portalEnabled: z.boolean().default(true),
  portalPaymentsEnabled: z.boolean().default(true),
  /** The trade template the workspace was set up from, if any. */
  tradeTemplate: tradeTemplate.nullable().optional(),
  automations: automationSettingsSchema.prefault({}),
});
export type WorkspaceSettings = z.infer<typeof workspaceSettingsSchema>;

export const workspaceSchema = z.object({
  id,
  name: z.string(),
  slug: z.string(),
  businessType,
  branding: brandingSchema,
  settings: workspaceSettingsSchema,
  plan: z.string(),
  stripeCustomerId: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  archivedAt: z.date().nullable(),
});
export type Workspace = z.infer<typeof workspaceSchema>;

/**
 * Optional business location at sign-up / workspace creation. `region` takes a province or state
 * code or name and is normalized to the code; when `country` is omitted it is inferred from the
 * region (Canada first), and a workspace without either defaults to Canada.
 */
const locationFields = {
  country: z.string().trim().toUpperCase().pipe(z.enum(SUPPORTED_COUNTRY_CODES as [SupportedCountry, ...SupportedCountry[]])).optional(),
  region: z.string().trim().max(60).optional(),
};

function locateRegion(country: SupportedCountry | undefined, region: string): { country: SupportedCountry; region: string } | null {
  for (const c of country ? [country] : (['CA', 'US'] as const)) {
    const code = normalizeRegion(c, region);
    if (code) return { country: c, region: code };
  }
  return null;
}

type Location = { country?: SupportedCountry; region?: string };

function normalizeLocation<V extends Location>(v: V): V {
  if (!v.region) return v;
  const found = locateRegion(v.country, v.region);
  return found ? { ...v, ...found } : v;
}

function checkLocation(v: Location, ctx: z.RefinementCtx): void {
  if (v.region && !locateRegion(v.country, v.region)) {
    const message =
      v.country === 'CA' ? ADDRESS_MESSAGES.CA.region : v.country === 'US' ? ADDRESS_MESSAGES.US.region : 'Choose a Canadian province or US state';
    ctx.addIssue({ code: 'custom', path: ['region'], message });
  }
}

export const signUpInput = z
  .object({
    name: nonEmpty(120),
    email,
    password: z.string().min(10, 'At least 10 characters').max(128),
    workspaceName: nonEmpty(120),
    businessType,
    trade: tradeTemplate.optional(),
    ...locationFields,
  })
  .overwrite(normalizeLocation)
  .superRefine(checkLocation);
export type SignUpInput = z.infer<typeof signUpInput>;

export const createWorkspaceInput = z
  .object({
    name: nonEmpty(120),
    slug: slug.optional(),
    businessType,
    trade: tradeTemplate.optional(),
    ...locationFields,
  })
  .overwrite(normalizeLocation)
  .superRefine(checkLocation);
export type CreateWorkspaceInput = z.infer<typeof createWorkspaceInput>;

export const updateWorkspaceInput = z.object({
  name: nonEmpty(120).optional(),
  slug: slug.optional(),
  businessType: businessType.optional(),
});
export type UpdateWorkspaceInput = z.infer<typeof updateWorkspaceInput>;

/** Partial update: omitted fields keep their stored value (defaults are not re-applied). */
export const updateSettingsInput = partialOf(workspaceSettingsSchema);
export type UpdateSettingsInput = z.infer<typeof updateSettingsInput>;

export const updateBrandingInput = brandingSchema;
export type UpdateBrandingInput = z.infer<typeof updateBrandingInput>;

// ---------------------------------------------------------------------------
// Users, memberships, invitations
// ---------------------------------------------------------------------------

export const userSchema = z.object({
  id,
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type User = z.infer<typeof userSchema>;

export const membershipSchema = baseRow.extend({
  userId: id,
  role: memberRole,
  status: membershipStatus,
  title: z.string().nullable(),
  permissionTemplateId: id.nullable(),
  /** CRM company a sub/supplier user works for (links their login to the vendor record). */
  companyId: id.nullable(),
});
export type Membership = z.infer<typeof membershipSchema>;

export const memberWithUserSchema = membershipSchema.extend({
  user: userSchema.pick({ id: true, name: true, email: true, image: true }),
});
export type MemberWithUser = z.infer<typeof memberWithUserSchema>;

export const updateMemberInput = z.object({
  id,
  role: memberRole.optional(),
  title: z.string().trim().max(120).nullable().optional(),
  status: membershipStatus.optional(),
  permissionTemplateId: id.nullable().optional(),
});
export type UpdateMemberInput = z.infer<typeof updateMemberInput>;

export const invitationSchema = baseRow.extend({
  email: z.string(),
  role: memberRole,
  status: invitationStatus,
  expiresAt: z.date(),
  invitedBy: id.nullable(),
  acceptedAt: z.date().nullable(),
  permissionTemplateId: id.nullable(),
  message: z.string().nullable(),
});
export type Invitation = z.infer<typeof invitationSchema>;

export const createInvitationInput = z.object({
  email,
  role: memberRole.exclude(['owner']),
  permissionTemplateId: id.nullable().optional(),
  message: optionalText(1000),
});
export type CreateInvitationInput = z.infer<typeof createInvitationInput>;

export const acceptInvitationInput = z.object({ token: z.string().min(16).max(256) });
export type AcceptInvitationInput = z.infer<typeof acceptInvitationInput>;

export const listInvitationsInput = listInput.extend({ status: invitationStatus.optional() });

// ---------------------------------------------------------------------------
// Permission templates
// ---------------------------------------------------------------------------

export const permissionTemplateSchema = baseRow.extend({
  name: z.string(),
  description: z.string().nullable(),
  baseRole: role,
  permissions: permissionSet,
  isSystem: z.boolean(),
});
export type PermissionTemplate = z.infer<typeof permissionTemplateSchema>;

export const createPermissionTemplateInput = z.object({
  name: nonEmpty(120),
  description: optionalText(1000),
  baseRole: role,
  permissions: permissionSet,
});
export type CreatePermissionTemplateInput = z.infer<typeof createPermissionTemplateInput>;
export const updatePermissionTemplateInput = updateOf(createPermissionTemplateInput);
export type UpdatePermissionTemplateInput = z.infer<typeof updatePermissionTemplateInput>;

// ---------------------------------------------------------------------------
// Role policies: a company's changes to what a whole role can do (Roles & permissions page).
// Stored as permission_template rows named `role:<role>`.
// ---------------------------------------------------------------------------

/** Roles a company can change. Owner always has everything; client is fixed to the portal. */
export const policyRole = z.enum(['admin', 'pm', 'sales', 'field', 'sub']);

/** One concrete permission, e.g. `invoice:approve` (no wildcards). */
export const permissionCell = z.string().refine((s) => {
  const [r, a, extra] = s.split(':');
  return extra === undefined && (RESOURCES as readonly string[]).includes(r ?? '') && (ACTIONS as readonly string[]).includes(a ?? '');
}, 'Expected resource:action');

export const setRolePolicyInput = z.object({
  role: policyRole,
  changes: z.array(z.object({ cell: permissionCell, allowed: z.boolean() })).max(400),
  /** Field and subcontractor only: whether they see and edit only their own records. */
  ownRecordsOnly: z.boolean().optional(),
  /** Version of the role's policy the page loaded (null/omitted when it had none). */
  expectedVersion: z.number().int().nonnegative().nullable().optional(),
});
export type SetRolePolicyInput = z.infer<typeof setRolePolicyInput>;

export const resetRolePolicyInput = z.object({
  role: policyRole,
  expectedVersion: z.number().int().nonnegative().nullable().optional(),
});
export type ResetRolePolicyInput = z.infer<typeof resetRolePolicyInput>;

// ---------------------------------------------------------------------------
// Custom fields
// ---------------------------------------------------------------------------

export const CUSTOM_FIELD_ENTITIES = [
  'company',
  'contact',
  'deal',
  'project',
  'estimate',
  'invoice',
  'change_order',
  'rfi',
  'submittal',
  'punch_item',
  'daily_log',
] as const;
export const customFieldEntity = z.enum(CUSTOM_FIELD_ENTITIES);
export type CustomFieldEntity = z.infer<typeof customFieldEntity>;

export const CUSTOM_FIELD_TYPES = [
  'text',
  'textarea',
  'number',
  'currency',
  'date',
  'boolean',
  'select',
  'multiselect',
  'url',
  'email',
  'phone',
] as const;
export const customFieldType = z.enum(CUSTOM_FIELD_TYPES);
export type CustomFieldType = z.infer<typeof customFieldType>;

export const customFieldDefinitionSchema = baseRow.extend({
  entity: customFieldEntity,
  key: z.string(),
  label: z.string(),
  type: customFieldType,
  options: z.array(z.string()),
  required: z.boolean(),
  position: z.number().int(),
  helpText: z.string().nullable(),
});
export type CustomFieldDefinition = z.infer<typeof customFieldDefinitionSchema>;

export const createCustomFieldInput = z.object({
  entity: customFieldEntity,
  key: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/, 'snake_case key'),
  label: nonEmpty(120),
  type: customFieldType,
  options: z.array(z.string().trim().min(1).max(120)).max(200).default([]),
  required: z.boolean().default(false),
  position: z.number().int().default(0),
  helpText: optionalText(500),
});
export type CreateCustomFieldInput = z.infer<typeof createCustomFieldInput>;
export const updateCustomFieldInput = updateOf(createCustomFieldInput).omit({ entity: true, key: true });
export type UpdateCustomFieldInput = z.infer<typeof updateCustomFieldInput>;

// ---------------------------------------------------------------------------
// Audit + notifications
// ---------------------------------------------------------------------------

export const ACTOR_TYPES = ['user', 'portal_client', 'api_key', 'system', 'integration'] as const;
export const actorType = z.enum(ACTOR_TYPES);
export type ActorType = z.infer<typeof actorType>;

export const auditEventSchema = baseRow.extend({
  actorId: id.nullable(),
  actorType,
  action: z.string(),
  entityType: z.string(),
  entityId: id.nullable(),
  projectId: id.nullable(),
  changes: z.record(z.string(), z.object({ from: z.unknown(), to: z.unknown() })).nullable(),
  metadata: z.record(z.string(), z.unknown()),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
});
export type AuditEvent = z.infer<typeof auditEventSchema>;

export const listAuditEventsInput = listInput.extend({
  entityType: z.string().max(64).optional(),
  entityId: id.optional(),
  actorId: id.optional(),
  action: z.string().max(128).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export type ListAuditEventsInput = z.infer<typeof listAuditEventsInput>;

export const notificationSchema = baseRow.extend({
  userId: id,
  type: z.string(),
  title: z.string(),
  body: z.string().nullable(),
  link: z.string().nullable(),
  entityType: z.string().nullable(),
  entityId: id.nullable(),
  readAt: z.date().nullable(),
});
export type Notification = z.infer<typeof notificationSchema>;

export const listNotificationsInput = listInput.extend({ unreadOnly: z.boolean().default(false) });
export const markNotificationsReadInput = z.object({ ids: z.array(id).max(500).optional() });

export const workspaceSummarySchema = z.object({
  id,
  name: z.string(),
  slug: z.string(),
  businessType,
  role: memberRole,
  logoKey: z.string().nullable().optional(),
});
export type WorkspaceSummary = z.infer<typeof workspaceSummarySchema>;

export const updateProfileInput = z.object({
  name: nonEmpty(120).optional(),
  image: z.string().max(2048).nullable().optional(),
});

export { email as emailSchema };
